import type { ColumnDef } from '@tanstack/react-table'
import { Trash2 } from 'lucide-react'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'

// This interface handles both coupon selection data and contract discount data
interface Discount {
  id: string
  coupon_id?: string | null
  name: string
  description?: string | null
  type: 'percentage' | 'fixed_amount'

  // From contract discounts API
  percentage_off?: number | null
  amount_off?: number | null
  currency?: string | null
  applies_to?: string
  valid_until?: string | null
  priority?: number
  stacking_behavior?: string
  status?: string
  applied_at?: string
  // biome-ignore lint/suspicious/noExplicitAny: required for dynamic metadata
  metadata?: Record<string, any>
  created_at?: string
  updated_at?: string

  // From coupon selection (CouponDisplayInfo format)
  discount?: number
  code?: string
  isValid?: boolean
  validityInfo?: string
}

interface DiscountsSectionProps {
  discounts: Discount[]
  onAddDiscount?: () => void
  onRemoveDiscount?: (discountId: string) => void
}

export function DiscountsSection({
  discounts,
  onAddDiscount,
  onRemoveDiscount,
}: DiscountsSectionProps) {
  const discountColumns: ColumnDef<Discount>[] = [
    {
      accessorKey: 'name',
      header: 'Coupon Name',
      size: 350,
      cell: ({ row }) => (
        <div>
          <div className='font-medium'>{row.original.name}</div>
          <div className='text-sm text-muted-foreground'>
            {row.original.description}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'discount',
      header: 'Discount',
      size: 120,
      cell: ({ row }) => {
        const discount = row.original

        // Handle coupon selection format (CouponDisplayInfo)
        if (discount.discount !== undefined) {
          return (
            <div className='font-medium'>
              {discount.type === 'percentage'
                ? `${discount.discount}%`
                : `$${discount.discount}`}
            </div>
          )
        }

        // Handle contract discounts API format
        if (
          discount.type === 'percentage' &&
          discount.percentage_off !== null &&
          discount.percentage_off !== undefined
        ) {
          return (
            <div className='font-medium'>
              {(discount.percentage_off * 100).toFixed(0)}%
            </div>
          )
        } else if (
          discount.type === 'fixed_amount' &&
          discount.amount_off !== null &&
          discount.amount_off !== undefined
        ) {
          return <div className='font-medium'>${discount.amount_off}</div>
        }

        return <div className='font-medium'>--</div>
      },
    },
    {
      accessorKey: 'type',
      header: 'Type',
      size: 120,
      cell: ({ row }) => <Badge variant='outline'>{row.original.type}</Badge>,
    },
    ...(onRemoveDiscount
      ? [
          {
            id: 'actions',
            header: 'Actions',
            size: 80,
            cell: ({ row }: { row: { original: Discount } }) => (
              <Button
                variant='ghost'
                size='sm'
                onClick={() => onRemoveDiscount(row.original.id)}
                className='text-destructive hover:text-destructive hover:bg-destructive/10'
              >
                <Trash2 className='w-4 h-4' />
              </Button>
            ),
          },
        ]
      : []),
  ]

  return (
    <Card className='bg-transparent border'>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <CardTitle>Discounts</CardTitle>
          {onAddDiscount && (
            <Button onClick={onAddDiscount} variant='outline' size='sm'>
              Add Discount
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {discounts.length === 0 ? (
          <div className='text-center py-8 text-muted-foreground'>
            <div className='border border-dashed border-muted-foreground/30 rounded-lg p-6'>
              <div
                className={`grid gap-4 text-sm ${onRemoveDiscount ? 'grid-cols-4' : 'grid-cols-3'}`}
              >
                <div>Coupon Name</div>
                <div>Discount</div>
                <div>Type</div>
                {onRemoveDiscount && <div>Actions</div>}
              </div>
              <div
                className={`grid gap-4 text-center py-4 text-muted-foreground ${onRemoveDiscount ? 'grid-cols-4' : 'grid-cols-3'}`}
              >
                <div>--</div>
                <div>--</div>
                <div>--</div>
                {onRemoveDiscount && <div>--</div>}
              </div>
            </div>
          </div>
        ) : (
          <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
            <EnhancedTable
              data={discounts}
              columns={discountColumns.map(col => ({
                ...col,
                enableSorting: false,
              }))}
              height='200px'
              className='bg-transparent'
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
